import { apiFetch } from '../lib/apiFetch';

export type PayRateRole = 'teacher' | 'assistant' | 'teacher_in_training' | 'travel_pay';

export type DefaultRateKey =
  | 'teacher'
  | 'assistant'
  | 'teacher_in_training'
  | 'teacher_on_assistant_shift';

export type RateSource = 'personal' | 'default' | 'none';

export type PayRateRefusal =
  | 'NO_PERMISSION'
  | 'PAY_RATE_OUT_OF_SCOPE'
  | 'PAY_RATE_SELF'
  | 'PAY_RATE_RANK';

export interface CurrentRate {
  role: PayRateRole;
  /** Null only when `source` is 'none'. */
  amountCents: number | null;
  source: RateSource;
  /** The personal row's date when personal, else null. */
  effectiveFrom: string | null;
  /** Set when `source` is 'default', null otherwise. */
  defaultRateKey: DefaultRateKey | null;
}

/** One personal pay-rate row. */
export interface PayRateEntry {
  id: string;
  role: PayRateRole;
  amountCents: number;
  /** YYYY-MM-DD. */
  effectiveFrom: string;
  /** Null for rows from the rate sheet import. */
  setBy: { userId: string; name: string } | null;
  /** ISO instant the row was written. */
  setAt: string;
  startsInFuture: boolean;
  removable: boolean;
}

export interface StaffRoleRates {
  role: PayRateRole;
  current: CurrentRate;
  /** The default that applies to this person for this role; null for travel pay. */
  defaultAmountCents: number | null;
  /** Newest effective date first, future rows included. */
  history: PayRateEntry[];
}

export interface StaffPayRates {
  userId: string;
  isSalaried: boolean;
  canEdit: boolean;
  /** Null exactly when `canEdit` is true. */
  editRefusal: PayRateRefusal | null;
  today: string;
  earliestEffectiveFrom: string;
  /** Always four entries, in the fixed role order. */
  roles: StaffRoleRates[];
}

/** A refused pay-rate request: the status, the stable `code` when the API sent
 *  one, and the per-field messages on a 400. */
export class PayRateRequestError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'PayRateRequestError';
  }
}

interface PayRateErrorBody {
  message?: string;
  code?: string;
  fieldErrors?: Record<string, string>;
}

async function payRatesFrom(res: Response): Promise<StaffPayRates> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as PayRateErrorBody;
    throw new PayRateRequestError(
      res.status,
      body.message ?? `Pay rate request failed: ${res.status}`,
      body.code,
      body.fieldErrors,
    );
  }
  return (await res.json()).payRates;
}

export async function fetchStaffPayRates(userId: string): Promise<StaffPayRates> {
  return payRatesFrom(await apiFetch(`/api/v1/staff/${userId}/pay-rates`));
}

export interface SetPayRateInput {
  role: PayRateRole;
  amountCents: number;
  /** YYYY-MM-DD. */
  effectiveFrom: string;
}

export async function setStaffPayRate(
  userId: string,
  input: SetPayRateInput,
): Promise<StaffPayRates> {
  return payRatesFrom(
    await apiFetch(`/api/v1/staff/${userId}/pay-rates`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export async function removeStaffPayRate(userId: string, rateId: string): Promise<StaffPayRates> {
  return payRatesFrom(
    await apiFetch(`/api/v1/staff/${userId}/pay-rates/${rateId}`, { method: 'DELETE' }),
  );
}

export async function setStaffSalaried(
  userId: string,
  isSalaried: boolean,
): Promise<StaffPayRates> {
  return payRatesFrom(
    await apiFetch(`/api/v1/staff/${userId}/salaried`, {
      method: 'PUT',
      body: JSON.stringify({ isSalaried }),
    }),
  );
}
